import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { type GetProps, getTokenValue, styled, type Token, View } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import type { Status } from "../shared-types/status";
import { pillTextTruncation, StyledPill, statusColorTokens } from "./StyledPill";
import { Interface2, Interface3 } from "./Typography";
export type BadgeSize = "small" | "medium";

// Geometry is Badge policy — StyledPill carries only the skin.
const BadgeFrame = styled(StyledPill, {
  name: "Badge",
  variants: {
    size: {
      medium: {
        height: "$1.5",
        paddingHorizontal: "$sp1"
      },
      small: {
        height: "$1",
        paddingHorizontal: "$sp0.5"
      }
    },
    status: {
      brand: {
        backgroundColor: "$bgBrandIntense",
        borderColor: "$bgBrandIntense"
      }
    },
    bordered: {
      true: {},
      false: {
        borderWidth: 0
      }
    }
  } as const,
  defaultVariants: {
    size: "medium"
  }
});
const getIconSizeToken = (size: BadgeSize): Token => `$${size === "small" ? "iconXSmall" : "iconSmall"}`;

// Optical padding: a leading/trailing icon carries its own visual whitespace,
// so the padding on the icon's side is halved vs the text side (per size).
const badgeIconSidePadding = {
  small: "$sp0.25",
  medium: "$sp0.5"
} as const satisfies Record<BadgeSize, string>;

// `interactive`/`disabled` are StyledPill variants Badge doesn't support — a
// static `role="status"` pill has no interactive states to toggle.
export type BadgeProps = Omit<GetProps<typeof BadgeFrame>, "children" | "interactive" | "disabled" | "status"> & {
  status?: Status | "brand";
  size?: BadgeSize;
  bordered?: boolean;
  iconLeft?: FunctionComponent<IconProps>;
  iconRight?: FunctionComponent<IconProps>;
  children: ReactNode;
};
const brandColorTokens = {
  text: "$textOnBrand",
  icon: "$iconOnBrand"
} as const;
const BadgeTextMedium = styled(Interface2, pillTextTruncation);
const BadgeTextSmall = styled(Interface3, pillTextTruncation);
export const Badge = (props: BadgeProps) => {
  const {
    status = "neutral",
    size = "medium",
    iconLeft: IconLeft,
    iconRight: IconRight,
    children,
    ...rest
  } = props;
  const iconSize = useMemo(() => getTokenValue(getIconSizeToken(size), "size") as number, [size]);
  const tokens = status === "brand" ? brandColorTokens : statusColorTokens[status];
  const BadgeText = size === "small" ? BadgeTextSmall : BadgeTextMedium;
  return <BadgeFrame {...rest} render="span" role="status" status={status} size={size} {...IconLeft ? {
    paddingLeft: badgeIconSidePadding[size]
  } : {}} {...IconRight ? {
    paddingRight: badgeIconSidePadding[size]
  } : {}} data-sentry-element="BadgeFrame" data-sentry-component="Badge" data-sentry-source-file="Badge.tsx">
      {IconLeft ? <View flexShrink={0}>
          <IconLeft size={iconSize} color={tokens.icon} />
        </View> : null}
      <BadgeText color={tokens.text} flexShrink={1} numberOfLines={1} data-sentry-element="BadgeText" data-sentry-source-file="Badge.tsx">
        {children}
      </BadgeText>
      {IconRight ? <View flexShrink={0}>
          <IconRight size={iconSize} color={tokens.icon} />
        </View> : null}
    </BadgeFrame>;
};